(() => {
  'use strict';

  window.__crossxMotion?.destroy();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileViewport = window.matchMedia('(max-width: 749px)');
  const records = new Map();
  let intersectionObserver;
  let resizeObserver;
  let frame = 0;
  let enabled = false;
  let destroyed = false;

  const clamp = (value, lower, upper) => Math.max(lower, Math.min(upper, value));
  const viewportHeight = () => window.innerHeight || document.documentElement.clientHeight;

  function schedule() {
    if (enabled && !document.hidden && !frame) frame = window.requestAnimationFrame(render);
  }

  function invalidate() {
    schedule();
  }

  function render() {
    frame = 0;
    if (!enabled || document.hidden) return;
    const screenHeight = viewportHeight();
    const isMobile = mobileViewport.matches;
    const updates = [];

    // Horizon scrolls .page-wrapper on desktop and the viewport on mobile.
    // Reading visible sections directly handles both without assuming window.scrollY.
    // Batch these few geometry reads before writing any layer styles.
    for (const record of records.values()) {
      if (!record.section.isConnected || !record.visible) continue;
      const { top, height } = record.section.getBoundingClientRect();
      if (!height || !screenHeight) continue;
      if (top >= screenHeight || top + height <= 0) continue;
      const progress = clamp((screenHeight - top) / (screenHeight + height), 0, 1);
      for (const layer of record.layers) {
        const distance = isMobile ? layer.mobileTravel : layer.travel;
        updates.push([layer, ((progress - .5) * distance * 2).toFixed(2) + 'px']);
      }
    }
    for (const [layer, value] of updates) {
      if (value === layer.lastValue) continue;
      layer.element.style.setProperty('--cx-parallax-y', value);
      layer.lastValue = value;
    }
    // Deliberately no recursive animation frame: scrolling drives the next update.
  }

  function resetRecord(record) {
    record.section.removeAttribute('data-cx-motion-ready');
    for (const layer of record.layers) layer.element.style.removeProperty('--cx-parallax-y');
  }

  function stop() {
    enabled = false;
    if (frame) window.cancelAnimationFrame(frame);
    frame = 0;
    window.removeEventListener('scroll', schedule);
    document.removeEventListener('scroll', schedule, true);
    window.removeEventListener('resize', invalidate);
    document.removeEventListener('load', invalidate, true);
    intersectionObserver?.disconnect();
    resizeObserver?.disconnect();
    for (const record of records.values()) resetRecord(record);
    records.clear();
  }

  function refresh() {
    stop();
    if (destroyed || reducedMotion.matches || window.Shopify?.designMode || !window.IntersectionObserver) return;
    const sections = [...document.querySelectorAll('[data-cx-parallax]')];
    if (!sections.length) return;

    intersectionObserver = new IntersectionObserver(entries => {
      for (const entry of entries) {
        const record = records.get(entry.target);
        if (!record) continue;
        record.visible = entry.isIntersecting;
      }
      schedule();
    }, { threshold: 0 });
    if (window.ResizeObserver) resizeObserver = new ResizeObserver(invalidate);

    for (const section of sections) {
      const layers = [...section.querySelectorAll('[data-cx-parallax-layer]')]
        .filter(element => element.closest('[data-cx-parallax]') === section)
        .map(element => {
          const requested = Number(element.dataset.cxTravel);
          const travel = Number.isFinite(requested) ? clamp(requested, -120, 120) : 30;
          const mobileRequested = Number(element.dataset.cxTravelMobile);
          const fallback = Math.sign(travel) * Math.min(30, Math.abs(travel) * .36);
          const mobileTravel = element.hasAttribute('data-cx-travel-mobile') && Number.isFinite(mobileRequested)
            ? clamp(mobileRequested, -40, 40) : fallback;
          return { element, travel, mobileTravel, lastValue: null };
        });
      if (!layers.length) continue;
      records.set(section, { section, layers, visible: false });
      section.setAttribute('data-cx-motion-ready', '');
      intersectionObserver.observe(section);
      resizeObserver?.observe(section);
    }
    if (!records.size) return;
    // Changes earlier on the page can shift a section without resizing that section.
    if (document.body) resizeObserver?.observe(document.body);
    enabled = true;
    window.addEventListener('scroll', schedule, { passive: true });
    // Element scroll events do not bubble; capture receives inner scroll containers.
    document.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', invalidate, { passive: true });
    document.addEventListener('load', invalidate, true);
    schedule();
  }

  function onVisibility() {
    if (document.hidden) {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
    } else invalidate();
  }

  function onSectionUnload(event) {
    for (const [section, record] of records) {
      if (event.target === section || event.target.contains?.(section)) {
        resetRecord(record);
        intersectionObserver?.unobserve(section);
        resizeObserver?.unobserve(section);
        records.delete(section);
      }
    }
    if (!records.size) stop();
    else invalidate();
  }

  function destroy() {
    destroyed = true;
    stop();
    reducedMotion.removeEventListener('change', refresh);
    mobileViewport.removeEventListener('change', invalidate);
    document.removeEventListener('DOMContentLoaded', refresh);
    document.removeEventListener('visibilitychange', onVisibility);
    document.removeEventListener('shopify:section:load', refresh);
    document.removeEventListener('shopify:section:unload', onSectionUnload);
    document.removeEventListener('shopify:section:reorder', invalidate);
    window.removeEventListener('pagehide', stop);
    window.removeEventListener('pageshow', refresh);
  }

  reducedMotion.addEventListener('change', refresh);
  mobileViewport.addEventListener('change', invalidate);
  document.addEventListener('visibilitychange', onVisibility);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', onSectionUnload);
  document.addEventListener('shopify:section:reorder', invalidate);
  window.addEventListener('pagehide', stop);
  window.addEventListener('pageshow', refresh);
  window.__crossxMotion = { refresh, destroy };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
  document.fonts?.ready.then(() => { if (!destroyed) invalidate(); });
})();
